import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack
} from '@mui/material';
import { usePreferences } from 'hooks/usePreferences';
import { formatDateWithPreferences } from 'utils/dateFormatUtils';
import { formatAmountForDisplayWithPreferences } from 'utils/index';

const PaymentsReceivedReport = ({ data, onPageChange }) => {
  const { preferences } = usePreferences();

  if (!data || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Payments Received Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Payments Received Report
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Date range: {formatDateWithPreferences(data.dateFrom, preferences)} to {formatDateWithPreferences(data.dateTo, preferences)}
      </Typography>
      {data.location && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Gross Received</Typography>
              <Typography variant="h5">{formatAmountForDisplayWithPreferences(data.summary.grossReceived || 0, preferences)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Refunds/Reversals</Typography>
              <Typography variant="h5" sx={{ color: (data.summary.refundsReversals || 0) > 0 ? 'error.main' : 'text.primary' }}>
                {formatAmountForDisplayWithPreferences(data.summary.refundsReversals || 0, preferences)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Net Received</Typography>
              <Typography variant="h5" sx={{ color: (data.summary.netReceived || 0) >= 0 ? 'success.main' : 'error.main' }}>
                {formatAmountForDisplayWithPreferences(data.summary.netReceived || 0, preferences)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Transactions</Typography>
              <Typography variant="h5">{data.summary.totalTransactions || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.payments || []).map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.invoiceNumber || 'N/A'}</TableCell>
                <TableCell>{payment.customerName || 'N/A'}</TableCell>
                <TableCell>{formatDateWithPreferences(payment.invoiceDate, preferences)}</TableCell>
                <TableCell>{formatDateWithPreferences(payment.paymentDate, preferences)}</TableCell>
                <TableCell>{payment.paymentMethod || 'N/A'}</TableCell>
                <TableCell align="right" sx={{ color: (payment.paymentAmount || 0) < 0 ? 'error.main' : 'text.primary' }}>
                  {formatAmountForDisplayWithPreferences(payment.paymentAmount || 0, preferences)}
                </TableCell>
              </TableRow>
            ))}
            {(!data.payments || data.payments.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">No payments found for the selected filters</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.pagination && data.pagination.totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {data.pagination.startIndex} to {data.pagination.endIndex} of {data.pagination.totalCount} payments
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Pagination
              count={data.pagination.totalPages}
              page={data.pagination.currentPage}
              onChange={(event, page) => onPageChange && onPageChange(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PaymentsReceivedReport;
